import React from 'react'
import { useRouter } from 'next/router'

// Helper component for SVG icons to avoid cluttering the main component
const Icons = {
  ChevronRight: () => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      className='h-5 w-5 ml-2 transition-transform group-hover:translate-x-1'
    >
      <path d='m9 18 6-6-6-6' />
    </svg>
  ),
  Quote: () => (
    <svg
      viewBox='0 0 288 220.02'
      xmlns='http://www.w3.org/2000/svg'
      className='w-10 absolute -top-2 -left-2 text-indigo-500'
      fill='currentColor'
    >
      <path d='M158.25 8.09h120.47v103.53c0 17.21-3.09 31.5-9.27 42.9-6.19 11.38-14.21 20.83-24.07 28.37-9.86 7.52-20.83 13.58-32.94 18.15-12.1 4.57-24.07 8.65-35.9 12.23l-17.75-32.26c8.97-3.41 16.63-6.86 22.99-10.35 6.37-3.5 11.57-7.31 15.6-11.43 4.04-4.13 6.99-8.84 8.88-14.11 1.89-5.3 2.82-11.52 2.82-18.69h-50.83V8.09zm-148.98 0h120.2v103.53c0 17.21-3.09 31.5-9.27 42.9-6.19 11.38-14.16 20.83-23.94 28.37-9.77 7.52-20.71 13.58-32.81 18.15-12.1 4.57-24.06 8.65-35.9 12.23l-18-32.26c8.97-3.41 16.63-6.86 22.99-10.35 6.37-3.5 11.57-7.31 15.6-11.43 4.04-4.13 6.99-8.84 8.88-14.11 1.89-5.3 2.82-11.52 2.82-18.69H9.27V8.09z' />
    </svg>
  ),
}

// Main landing page component
const SurveyLandingPage = () => {
  const router = useRouter()

  const handleTakeSurveyClick = () => {
    // This logic runs on the client-side to capture UTM params from the URL
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search)
      const utmParams = new URLSearchParams()

      const utmSource = urlParams.get('utm_source')
      const utmMedium = urlParams.get('utm_medium')
      const utmCampaign = urlParams.get('utm_campaign')

      if (utmSource) utmParams.append('utm_source', utmSource)
      if (utmMedium) utmParams.append('utm_medium', utmMedium)
      if (utmCampaign) utmParams.append('utm_campaign', utmCampaign)

      const utmString = utmParams.toString()
      const surveyUrl = `https://form.typeform.com/to/gCFSS3ML${
        utmString ? `?${utmString}` : ''
      }`

      router.push(surveyUrl)
    } else {
      // Fallback for server-side rendering, though the click is a client-side event.
      router.push('https://form.typeform.com/to/gCFSS3ML')
    }
  }
  return (
    <div className='bg-gray-50'>
      <section className='py-20 sm:py-24 lg:py-32'>
        <div className='container-medium'>
          <div className='flex items-center justify-between gap-12 flex-col-reverse md:flex-row'>
            <div className='text-center lg:text-left'>
              <h1 className='text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight'>
                Weź udział w badaniu
                <br />
                <span className='text-indigo-600'>Diversity w Polskim IT</span>
              </h1>
              <p className='mt-6 text-lg sm:text-xl text-gray-600 max-w-xl mx-auto lg:mx-0'>
                Twoja anonimowa opinia jest kluczowa. Trzeci rok z rzędu
                sprawdzamy, gdzie polityka firm mija się z rzeczywistością.
                Twoja anonimowa opinia pomoże oddzielić fakty od PR-u.
              </p>
              <div className='mt-10'>
                <button
                  type='button'
                  onClick={handleTakeSurveyClick}
                  className='bg-primary px-12 py-4 text-white rounded-full text-2xl font-bold duration-300 hover:bg-primary-hover'
                >
                  Wypełnij anonimową ankietę
                </button>
              </div>
              <div className='mt-12 md:mt-24 flex items-center md:items-start flex-col justify-normal font-thin'>
                <span>Partner Badania</span>
                <img
                  src='/next-images/reports/diversity/capgemini-logo.png'
                  alt='Capgemini - Partner Badania Diversity w polskim IT 2025'
                  className='w-72'
                  width={700}
                  height={164}
                />
              </div>
            </div>
            <div className='flex justify-center items-center'>
              <img
                src='/next-images/reports/diversity/logo-full.png'
                alt='Diversity w polskim IT 2025'
                className='w-36 md:w-full max-w-lg'
                width={1303}
                height={1595}
              />
            </div>
          </div>
        </div>
      </section>

      <section className='py-20 sm:py-24 lg:py-32 bg-action'>
        <div className='container'>
          <div className='max-w-4xl mx-auto text-white'>
            <div className='text-center'>
              <h2 className='text-3xl font-bold text-gray-800'>
                Dlaczego warto wziąć udział?
              </h2>
              <p className='mt-4 text-lg sm:text-xl leading-relaxed'>
                Dane od społeczności, nie korporacyjne hasła. Pokażmy, jak jest
                naprawdę.
              </p>
            </div>
            <div className='mt-16 grid md:grid-cols-2 gap-x-12 gap-y-10 text-black'>
              <div className='bg-white p-6 rounded-lg shadow-sm'>
                <h4 className='text-xl font-bold text-gray-800 mb-3'>
                  Twój głos ma znaczenie
                </h4>
                <p className='text-gray-600'>
                  Pytamy o to, co ważne, by poznać zdanie i doświadczenie osób z
                  każdej grupy: od osób z niepełnosprawnością przez
                  neuroatypowych, do osób, które nie identyfikują się z żadną z
                  takich grup.
                </p>
              </div>
              <div className='bg-white p-6 rounded-lg shadow-sm'>
                <h4 className='text-xl font-bold text-gray-800 mb-3'>
                  Konfrontujemy perspektywy
                </h4>
                <p className='text-gray-600'>
                  Zestawimy odpowiedzi specjalistów i menedżerów. Zobaczymy
                  czarno na białym, czy wizja managementu na temat inkluzywności
                  pokrywa się z codziennością w zespołach. To unikalna szansa,
                  by Twój głos został policzony i porównany.
                </p>
              </div>
              <div className='bg-white p-6 rounded-lg shadow-sm'>
                <h4 className='text-xl font-bold text-gray-800 mb-3'>
                  Co zrobimy z danymi?
                </h4>
                <p className='text-gray-600'>
                  To już trzecia edycja badania. Wszystkie zebrane dane (w 100%
                  anonimowe) trafią do publicznego raportu. Zobaczysz dane i
                  trendy, które kształtują polskie IT.
                </p>
              </div>
              <div className='bg-white p-6 rounded-lg shadow-sm'>
                <h4 className='text-xl font-bold text-gray-800 mb-3'>
                  Ile czasu potrzebujesz?
                </h4>
                <p className='text-gray-600'>
                  Wypełnienie ankiety zajmuje średnio 5 minut.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className='py-20 sm:py-24'>
        <div className='container-medium'>
          <h2>Partner badania</h2>
          <div className='relative mt-8'>
            <img
              src='/next-images/reports/diversity/capgemini.png'
              alt='Partner background'
              className='w-full h-auto object-cover'
              width={2400}
              height={800}
            />
            <div className='relative bg-white p-10 md:p-12 text-justify font-thin text-xl'>
              {/* Top section with logo, positioned over the backdrop */}
              <div className='absolute left-1/2 md:left-10 -top-[80px] -translate-x-1/2 md:-translate-x-0 w-11/12 sm:w-auto'>
                <div className='bg-white p-5 max-w-sm mx-auto md:mx-0'>
                  <img
                    src='/next-images/reports/diversity/capgemini-logo.png'
                    alt='Capgemini'
                    className='h-20 object-contain w-full'
                    width={700}
                    height={164}
                  />
                </div>
              </div>

              {/* Spacer for the absolutely positioned logo on mobile */}
              <div className='h-16 md:hidden' />

              <p className='mb-8'>
                W Capgemini stawiamy na różnorodność, bo wiemy, że napędza ona
                innowacje i otwiera drzwi do prawdziwego rozwoju i kreatywności.
                Nasze motto „Inclusive Future for all” to nie tylko zgrabne
                hasło, ale wyznacznik kierunku realnych działań, które aktywnie
                wspierają kulturę inkluzywną i pozwalają tworzyć włączające
                środowisko pracy.
              </p>
              <p className='mb-8'>
                Od wielu lat w ramach działań CSR realizujemy szereg ważnych
                inicjatyw edukacyjnych i aktywizujących naszych pracowników i
                pracownice w zakresie Diversity & Inclusion. Nieustannie
                wsłuchujemy się w potrzeby naszych zespołów i dajemy im
                możliwość realnego wpływu na kulturę firmy. Takie podejście
                pomaga nam w budowaniu przestrzeni wolnej od uprzedzeń, w której
                każdy głos ma znaczenie, a ludzie mogą być sobą i rozwijać się
                na własnych zasadach – niezależnie od swoich zdolności,
                pochodzenia, tożsamości płciowej, orientacji seksualnej czy
                wyznania.
              </p>
              <p className='mb-8'>
                W polskim oddziale Capgemini działa dziś sześć sieci
                pracowniczych (communities), które aktywnie wspierają działania
                z zakresu D&I, a dzięki nim ludzie z różnych środowisk mogą się
                spotkać, wspierać i inspirować nawzajem, wyrażać swoje opinie.
                Są to: OUTfront (wspierające działania na rzecz osób LGBT+),
                Women@Capgemini (wspomagające rozwój zawodowy i osobisty
                kobiet), Men@Capgemini (promujące zdrowie i rozwój mężczyzn),
                CulturALL (działające na rzecz osób z różnych kręgów etnicznych
                i kulturowych), CapAbility (skupiające się na potrzebach osób z
                niepełnosprawnościami) oraz Neuroability Community (aktywnie
                promujące bezpieczną przestrzeń dla osób neuroatypowych).{' '}
              </p>

              {/* Testimonial Section */}
              <div className='mt-8 pt-8 border-t border-gray-200'>
                <div className='flex flex-col md:flex-row items-center md:items-start text-center md:text-left'>
                  <div className='relative w-44 h-44 flex-shrink-0 mb-6 md:mb-0'>
                    <img
                      src='/next-images/reports/diversity/ewa-krupka.jpg'
                      alt='Ewa Krupka'
                      className='w-full h-full rounded-full object-cover shadow-md'
                      width={500}
                      height={500}
                    />
                    <Icons.Quote />
                  </div>
                  <div className='md:pl-8'>
                    <p className='italic mb-4'>
                      „Dla mnie inkluzywność to coś, co dzieje się na co dzień.
                      Przejawia się w rozmowach z kolegami i koleżankami z
                      zespołów, z naszymi klientami, w tym, jak się traktujemy.
                      W Capgemini naprawdę widać, że tu nie chodzi tylko o
                      hasła, ale o ludzi. Dzięki naszym społecznościom i
                      codziennym działaniom budujemy miejsce, gdzie każdy ma
                      przestrzeń, żeby być sobą i rozwijać się na własnych
                      zasadach. A to ma ogromne znaczenie, nie tylko dla tych
                      osób, które są na początku swojej kariery, ale i dla tych,
                      które są z nami od lat. Chcemy, żeby przyszłość – bardziej
                      różnorodna i otwarta – zaczynała się właśnie tutaj.”
                    </p>
                    <p className='font-semibold'>Ewa Krupka</p>
                    <p className='text-gray-500 italic'>
                      Junior CSR Manager z Capgemini Polska
                    </p>
                  </div>
                </div>
              </div>

              <div className='text-right mt-8'>
                <a
                  href='https://bulldogjob.pl/companies/profiles/615-capgemini'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='group inline-flex items-center font-semibold text-indigo-600 hover:text-indigo-800 transition-colors duration-300'
                >
                  Dowiedz się więcej o Capgemini
                  <Icons.ChevronRight />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className='py-20'>
        <div className='container-medium'>
          <div className='text-center'>
            <h2 className='text-3xl sm:text-4xl font-bold text-gray-900'>
              Jesteś gotów/gotowa podzielić się swoją opinią?
            </h2>
            <p className='mt-4 text-xl text-gray-600 max-w-2xl mx-auto'>
              Twoje doświadczenie jest cenne, niezależnie od tego, czy na co
              dzień myślisz o D&I, czy uważasz to za temat drugorzędny. Dołącz
              do tysięcy profesjonalistów z branży i pomóż nam stworzyć
              najbardziej kompleksowy i szczery raport o różnorodności w polskim
              IT.
            </p>
            <div className='mt-10'>
              <button
                type='button'
                onClick={handleTakeSurveyClick}
                className='bg-primary px-12 py-4 text-white rounded-full text-2xl font-bold duration-300 hover:bg-primary-hover'
              >
                Przejdź do anonimowej ankiety
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default SurveyLandingPage
